import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useEffect } from 'react';
import {
  UserKeys,
  getDriveFolders,
  getDriveRootId,
} from '../../../../api/users-api';
import GeneralDialog from '../../../../shared/general-dialog';
import DriveFolderTree from '../../../user-management/user-details/drive/drive-folder-tree';
import Box from '@mui/material/Box';

interface DriveTreeProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedFolder: string;
  setSelectedFolder: Dispatch<SetStateAction<string>>;
  onSelection: () => void;
}

export default function DriveTreeDialog({
  open,
  handleClose,
  userEmail,
  selectedFolder,
  setSelectedFolder,
  onSelection,
}: DriveTreeProps) {
  const {
    data: driveId,
    isLoading: driveIdLoading,
    isFetching: driveIdFetching,
  } = useQuery({
    queryKey: [UserKeys.DriveRootId, userEmail],
    queryFn: () => getDriveRootId(userEmail),
    enabled: userEmail !== '',
  });

  //   have it selected initially
  useEffect(() => {
    if (driveId) {
      setSelectedFolder(driveId);
    }
  }, [driveId, setSelectedFolder]);

  const isTrashStarredOrSharedWithMe =
    selectedFolder === `${driveId}.shared` ||
    selectedFolder === `${driveId}.trash` ||
    selectedFolder === `${driveId}.starred`;

  const {
    data: allFolders,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: isLoadingFolderTree,
  } = useInfiniteQuery({
    queryKey: [UserKeys.DriveFolders, userEmail],
    queryFn: ({ pageParam }) => {
      return getDriveFolders(
        userEmail,
        pageParam ? pageParam.split('.')[0] : '',
        false,
        false,
        false,
        false,
        'name'
      );
    },
    initialPageParam: driveId,
    getNextPageParam: () => {
      return selectedFolder;
    },
    enabled:
      driveId !== undefined &&
      driveId !== '' &&
      userEmail !== '' &&
      !isTrashStarredOrSharedWithMe &&
      selectedFolder !== '',
    meta: {
      errorMessage:
        'There was an issue loading folder data. Please refresh and try again.',
    },
  });

  useEffect(() => {
    if (
      userEmail !== '' &&
      selectedFolder !== '' &&
      !isFetchingNextPage &&
      !allFolders?.pageParams.includes(selectedFolder)
    ) {
      fetchNextPage();
    }
  }, [
    allFolders?.pageParams,
    fetchNextPage,
    isFetchingNextPage,
    selectedFolder,
    userEmail,
  ]);

  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      title="Select Folder"
      dialogContent={
        <Box
          sx={{ width: 400, minHeight: 200, maxHeight: 300, overflowY: 'auto' }}
        >
          <DriveFolderTree
            driveId={driveId || ''}
            userEmail={userEmail}
            setSelectedFolder={setSelectedFolder}
            selectedFolder={selectedFolder}
            allFolders={allFolders}
            isLoading={isLoadingFolderTree || driveIdLoading || driveIdFetching}
            displayJustDrive
            disabledNodes={[]}
          />
        </Box>
      }
      primaryButton={{
        title: 'Select',
        action: onSelection,
        loading: false,
        disabled:
          isLoadingFolderTree ||
          driveIdLoading ||
          driveIdFetching ||
          selectedFolder === '' ||
          selectedFolder === driveId,
      }}
    />
  );
}
